import type { RowData } from "@tanstack/react-table";
import type {
    DataGridCell,
    DataGridRow,
    DataGridTable,
} from "../columns/types";
import { getDisplayColumns, getFocusedAddress } from "../focus/navigation";
import type { DataGridCellAddress } from "../types";
import { getCell } from "./cellAccess";

/** Rectangle in display order: inclusive row and column indexes. */
export interface CellRect {
    top: number;
    bottom: number;
    left: number;
    right: number;
}

export interface GridPoint {
    rowIndex: number;
    columnIndex: number;
}

/** The active range (or the active cell) as a display-order rectangle. */
export const getActiveRect = <TData extends RowData>(
    table: DataGridTable<TData>,
): CellRect | null => {
    const bounds = table.getCellSelectionBounds()[0];
    return bounds
        ? {
              top: bounds.minRowIndex,
              bottom: bounds.maxRowIndex,
              left: bounds.minColumnIndex,
              right: bounds.maxColumnIndex,
          }
        : null;
};

/** The smallest rectangle holding every address, or `null` when there are none or one is gone. */
export const getBoundingRect = <TData extends RowData>({
    table,
    addresses,
}: {
    table: DataGridTable<TData>;
    addresses: readonly DataGridCellAddress[];
}): CellRect | null => {
    const points = addresses.map((address) => ({
        row: getCell(table, address)?.row.getDisplayIndex() ?? -1,
        column: getColumnIndex(table, address.columnId),
    }));
    if (
        points.length === 0 ||
        points.some((point) => point.row < 0 || point.column < 0)
    ) {
        return null;
    }
    const rows = points.map((point) => point.row);
    const columns = points.map((point) => point.column);
    return {
        top: Math.min(...rows),
        bottom: Math.max(...rows),
        left: Math.min(...columns),
        right: Math.max(...columns),
    };
};

/** Finds cells by display-order point, reading rows and columns once for a loop over many cells. */
export const createCellLookup = <TData extends RowData>(
    table: DataGridTable<TData>,
) => {
    const rows: ReadonlyArray<DataGridRow<TData>> =
        table.getRowsInDisplayOrder();
    const columns = getDisplayColumns(table);
    return ({
        rowIndex,
        columnIndex,
    }: GridPoint): DataGridCell<TData> | undefined => {
        const column = columns[columnIndex];
        return column
            ? rows[rowIndex]?.getAllCellsByColumnId()[column.id]
            : undefined;
    };
};

/** The rows and display columns a rectangle covers, clamped to the grid. */
export const getRectRowsAndColumns = <TData extends RowData>({
    table,
    rect,
}: {
    table: DataGridTable<TData>;
    rect: CellRect;
}) => {
    const rows: ReadonlyArray<DataGridRow<TData>> =
        table.getRowsInDisplayOrder();
    return {
        rows: rows.slice(rect.top, rect.bottom + 1),
        columns: getDisplayColumns(table).slice(rect.left, rect.right + 1),
    };
};

/** The selectable cells a rectangle covers, row by row, without empty rows. */
export const getRectCells = <TData extends RowData>(input: {
    table: DataGridTable<TData>;
    rect: CellRect;
}) => {
    const { rows, columns } = getRectRowsAndColumns(input);
    const cellRows = rows.map((row) => {
        const cellsById = row.getAllCellsByColumnId();
        return columns
            .map((column) => cellsById[column.id])
            .filter((cell) => cell !== undefined && cell.getCanSelect());
    });
    return cellRows.filter((cells) => cells.length > 0);
};

/**
 * Selects a rectangle and keeps the active cell where it is. TanStack's
 * active cell is the last range's anchor, so the rectangle goes first and the
 * active cell follows as its own one-cell range inside it. False when there is
 * no active cell or the rectangle is out of bounds, which selects nothing.
 */
export const selectRect = <TData extends RowData>({
    table,
    rect,
}: {
    table: DataGridTable<TData>;
    rect: CellRect;
}): boolean => {
    const { rows, columns } = getRectRowsAndColumns({ table, rect });
    const top = rows[0];
    const bottom = rows[rect.bottom - rect.top];
    const left = columns[0];
    const right = columns[rect.right - rect.left];
    const active = getFocusedAddress(table.atoms.cellSelection.get());
    if (!top || !bottom || !left || !right || !active) {
        return false;
    }
    table.setCellSelection([
        {
            anchorRowId: top.id,
            anchorColumnId: left.id,
            focusRowId: bottom.id,
            focusColumnId: right.id,
        },
        {
            anchorRowId: active.rowId,
            anchorColumnId: active.columnId,
            focusRowId: active.rowId,
            focusColumnId: active.columnId,
        },
    ]);
    return true;
};

/** A range, not just the active cell, is selected. */
export const hasMultiCellRange = <TData extends RowData>(
    table: DataGridTable<TData>,
) => {
    const rect = getActiveRect(table);
    return (
        rect !== null && (rect.top !== rect.bottom || rect.left !== rect.right)
    );
};

/** Display index of a column, as cell selection counts it. */
export const getColumnIndex = <TData extends RowData>(
    table: DataGridTable<TData>,
    columnId: string,
) => table.getCellSelectionColumnIndexes()[columnId] ?? -1;
